import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import { retrieveAllStakeholdersApi } from "../../../../components/api/StakeholderApiService";
import { retrieveOrdersShippingViewApi } from "../../../../components/api/OrderApiService";

export default function ExportShippingView({
  orderType = "VENDOR",
  buttonLabel = "Shipping View Export",
  buttonProps = {},
}) {
  const [open, setOpen] = useState(false);
  const [stakeholders, setStakeholders] = useState([]);
  const [loadingStakeholders, setLoadingStakeholders] = useState(false);
  const [selectedStakeholderId, setSelectedStakeholderId] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchStakeholders();
  }, [open]);

  const fetchStakeholders = async () => {
    setLoadingStakeholders(true);
    try {
      const resp = await retrieveAllStakeholdersApi(orderType);
      setStakeholders(resp?.data || []);
    } catch (err) {
      console.error("Failed to load stakeholders", err);
      toast.error("Failed to load stakeholders");
    } finally {
      setLoadingStakeholders(false);
    }
  };

  const sanitizeFilename = (name) =>
    String(name || "")
      .replace(/[\r\n]+/g, " ")
      .replace(/[<>:"/\\|?*\u0000-\u001F]+/g, "_")
      .trim()
      .slice(0, 120);

  const formatNumberGerman = (value) => {
    try {
      if (value === null || value === undefined || value === "") return "";
      if (typeof value === "number" && !Number.isNaN(value)) {
        const s = String(value);
        const decimals = s.includes(".") ? s.split(".")[1].length : 0;
        return new Intl.NumberFormat("de-DE", {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(value);
      }
      if (typeof value === "string") {
        const cleaned = value.trim();
        if (/^-?\d+(\.\d+)?$/.test(cleaned)) {
          const num = Number(cleaned);
          const decimals = cleaned.includes(".") ? cleaned.split(".")[1].length : 0;
          return new Intl.NumberFormat("de-DE", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          }).format(num);
        }
      }
    } catch (e) {}
    return value;
  };

  // IMPORTANT: return numeric values as actual numbers (not formatted strings)
  // numeric strings are converted to Number as well. Sales/purchase order numbers remain raw strings.
  const normalizeValue = (v) => {
    if (v === null || v === undefined) return "";
    if (typeof v === "number") return v; // keep numbers as numbers
    if (typeof v === "boolean") return v;
    if (typeof v === "string") {
      const trimmed = v.trim();
      if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed); // numeric strings -> Number
      return v;
    }
    if (Array.isArray(v))
      return v
        .map((item) =>
          item && typeof item === "object" ? JSON.stringify(item) : String(item)
        )
        .join(", ");
    try {
      if (typeof v.toString === "function") return v.toString();
    } catch (e) {}
    return JSON.stringify(v);
  };

  const handleExport = async () => {
    if (!selectedStakeholderId) {
      toast.error("Please select a stakeholder");
      return;
    }
    setExporting(true);
    try {
      const resp = await retrieveOrdersShippingViewApi(
        selectedStakeholderId,
        orderType
      );
      const data = resp?.data || [];
      console.log("Shipping view response (raw):", data);

      if (!Array.isArray(data) || data.length === 0) {
        toast.error("No data to export for the selected stakeholder.");
        setExporting(false);
        return;
      }

      let headers = [];
      let rows = [];

      if (Array.isArray(data[0])) {
        const maxLen = Math.max(...data.map((r) => (Array.isArray(r) ? r.length : 1)));
        headers = Array.from({ length: maxLen }, (_, i) => `col${i + 1}`);
        rows = data.map((r) =>
          Array.isArray(r) ? r.map((v) => normalizeValue(v)) : [normalizeValue(r)]
        );
      } else if (typeof data[0] === "object") {
        const flattenRaw = (obj, parent = "", out = {}) => {
          Object.entries(obj || {}).forEach(([k, v]) => {
            const key = parent ? `${parent}.${k}` : k;
            if (v && typeof v === "object" && !Array.isArray(v)) {
              flattenRaw(v, key, out);
            } else {
              out[key] = v;
            }
          });
          return out;
        };

        const flattenedRaw = data.map((item) => flattenRaw(item));

        // keys that should keep their raw string value (no number formatting)
        const noFormatBases = ["itemCrop","salesOrderNumber", "purchaseOrderNumber"];
        const isNoFormatKey = (k) =>
          noFormatBases.includes(String(k).split(".").pop().toString().trim());

        // apply normalization but preserve raw strings for salesOrderNumber and purchaseOrderNumber
        const flattened = flattenedRaw.map((obj) => {
          const out = {};
          Object.entries(obj).forEach(([k, v]) => {
            if (isNoFormatKey(k)) {
              out[k] = v === null || v === undefined ? "" : String(v);
            } else {
              out[k] = normalizeValue(v);
            }
          });
          return out;
        });

        // preferred key order for columns (used to place matching dotted keys in this sequence)
        const compositeKeys = [
          "orderId",
          "itemId",
          "itemBuyingGrade",
          "itemOrigin",
          "itemCrop",
          "salesOrderNumber",
          "purchaseOrderNumber",
          "OrderVolume",
          "orderVolume",
          "orderRemainingQuantity",
          "shipmentRequestId",
          "shipmentRequestType",
          "shipmentRequestRecordStatus",
          "shippingInstructionId",
          "finalShippingInstructionNumber",
          "shipmentQuantity",
          "shippingInstructionStatus",
          "billOfLadingNumber",
        ];

        const allKeys = Array.from(
          new Set(flattened.flatMap((r) => Object.keys(r)))
        );

        const orderedOther = [];
        const remainingSet = new Set(allKeys);

        compositeKeys.forEach((base) => {
          const match = allKeys.find(
            (k) =>
              k.toLowerCase() === base.toLowerCase() ||
              k.toLowerCase().endsWith(`.${base.toLowerCase()}`)
          );
          if (match && remainingSet.has(match)) {
            orderedOther.push(match);
            remainingSet.delete(match);
          }
        });

        const remaining = Array.from(remainingSet).sort((a, b) => a.localeCompare(b));

        // NOTE: do NOT include a generated "order" composite column — place requested keys first, then others
        headers = [...orderedOther, ...remaining];
        rows = flattened.map((rowObj) => headers.map((h) => rowObj[h] ?? ""));
      } else {
        headers = ["value"];
        rows = data.map((d) => [normalizeValue(d)]);
      }

      if (headers.length === 0) {
        toast.error("Export data has no recognizable fields.");
        setExporting(false);
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("ShippingView");
      const headerRow = sheet.addRow(headers);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true };
      });

      // Add rows and keep numeric values as numbers in Excel.
      // For numeric cells set a sensible number format (thousands + decimals).
      rows.forEach((r) => {
        const rowArray = Array.from({ length: headers.length }, (_, i) =>
          r[i] === null || r[i] === undefined ? "" : r[i]
        );
        const added = sheet.addRow(rowArray);
        added.eachCell((cell, colNumber) => {
          const raw = r[colNumber - 1];
          if (typeof raw === "number" && !Number.isNaN(raw)) {
            // determine decimals from original numeric string if available, else from number
            let decimals = 0;
            try {
              // if original value came from a numeric string like "1.230", we likely converted to Number and lost trailing zeros.
              // derive decimals from the numeric value's string if it has a decimal point.
              const s = String(raw);
              decimals = s.includes(".") ? s.split(".")[1].length : 0;
            } catch (e) {
              decimals = 0;
            }
            const fmt = decimals > 0 ? `#,##0.${"0".repeat(decimals)}` : "#,##0";
            cell.numFmt = fmt;
          }
        });
      });

      sheet.columns = headers.map((h) => ({
        header: h,
        width: Math.min(40, Math.max(10, h.length + 8)),
      }));

      const stakeholderName =
        stakeholders.find((s) => s.id === selectedStakeholderId)?.name ||
        selectedStakeholderId;
      const filename = `ShippingView-${sanitizeFilename(orderType)}-${sanitizeFilename(
        stakeholderName
      )}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Exported shipping view to Excel");
      setOpen(false);
    } catch (err) {
      toast.error("Failed to export shipping view");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Button
        {...buttonProps}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen(true);
        }}
      >
        {buttonLabel}
      </Button>

      <Dialog
        open={open}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          onClick: (e) => e.stopPropagation(),
        }}
      >
        <DialogTitle>Export Shipping View ({orderType})</DialogTitle>
        <DialogContent>
          {loadingStakeholders ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {stakeholders.length === 0 ? (
                <Typography>No stakeholders found</Typography>
              ) : (
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Autocomplete
                    options={stakeholders}
                    getOptionLabel={(s) => s.name || `${s.code || ""} (${s.id})`}
                    value={
                      stakeholders.find((s) => s.id === selectedStakeholderId) ||
                      null
                    }
                    onChange={(e, value) => {
                      if (e) {
                        e.stopPropagation();
                        e.preventDefault();
                      }
                      setSelectedStakeholderId(value?.id || "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Stakeholder"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      />
                    )}
                    filterSelectedOptions
                    disableClearable={false}
                    clearOnBlur={false}
                  />
                </FormControl>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            disabled={exporting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleExport();
            }}
            disabled={exporting || loadingStakeholders}
          >
            {exporting ? <CircularProgress size={20} /> : "Export"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}